import type { CommentableContentObject, Reply } from 'models';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import {
  BookmarkFillIcon,
  BookmarkLineIcon,
  Button,
  Delete2LineIcon,
  Dropdown,
  Flag1LineIcon,
  LinkLine,
  type MenuItemType,
  Modal,
  More2LineIcon,
  PencilLineIcon,
  Space,
  message,
} from 'ui';
import { copyCommentLinkToClipboard } from 'features/copy-to-clipboard';
import { useCreateAbuseReport } from 'features/report';
import { useGetCommentParentId } from '../../../hooks';
import { useBookmarkCommentMutation, useDeleteCommentMutation, useUnbookmarkCommentMutation } from '../../../queries';

type P = {
  canDeleteComment: (comment: Reply) => boolean;
  comment: Reply;
  contentObjectId: string;
  contentObjectType: CommentableContentObject['type'];
  onEditObject: () => void;
  topParentShareableId?: string;
};

const CommentActions = ({
  canDeleteComment,
  comment,
  contentObjectId,
  contentObjectType,
  onEditObject,
  topParentShareableId,
}: P) => {
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const parentId = useGetCommentParentId({ comment, contentObjectId });
  const { canEditComment, canReportContentObject } = usePermissions();
  const { isPending: isBookmarking, mutate: bookmarkComment } = useBookmarkCommentMutation();
  const { isPending: isUnbookmarking, mutate: unbookmarkComment } = useUnbookmarkCommentMutation();
  const { mutateAsync: reportComment } = useCreateAbuseReport();
  const { isPending: isDeleting, mutate: deleteComment } = useDeleteCommentMutation();

  const handleBookmark = (reply: Reply) => {
    reply.bookmarked
      ? unbookmarkComment({ contentObjectId, id: comment.id })
      : bookmarkComment({ contentObjectId, id: comment.id });
  };

  const copyLink = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      if (!topParentShareableId || !contentObjectType) {
        message.error('Sorry, something went wrong.');
        return;
      }
      await copyCommentLinkToClipboard({ comment, parentId, topParentShareableId, topParentType: contentObjectType });
      message.success('Link copied to clipboard');
    } catch {
      message.error('Sorry, something went wrong.');
    }
  };

  const handleReport = () => {
    modal.confirm({
      okText: 'Report',
      async onOk() {
        await reportComment(
          { contentObjectId: comment.id },
          {
            onError() {
              message.error('Something went wrong.');
            },
            onSuccess() {
              message.success('Successfully reported.');
            },
          },
        );
      },
      title: 'Report this Post/Event/Comment?',
    });
  };

  const onSelectKey = async ({ key }: { key: string }) => {
    switch (key) {
      case 'delete':
        deleteComment({ contentObjectId, id: comment.id });
        break;
      case 'edit':
        onEditObject();
        break;
      case 'link':
        await copyLink();
        break;
      case 'report':
        handleReport();
        break;
      default:
        throw new Error(t('Unsupported option: {{option}}', { option: key }));
    }
  };

  const menu = () => {
    const actions: MenuItemType[] = [
      {
        icon: <LinkLine size="small" />,
        key: 'link',
        label: t('Copy link'),
      },
    ];

    if (canEditComment({ comment })) {
      actions.push({
        icon: <PencilLineIcon size="small" />,
        key: 'edit',
        label: t('Edit'),
      });
    }

    if (canReportContentObject({ contentObject: comment })) {
      actions.push({
        icon: <Flag1LineIcon size="small" />,
        key: 'report',
        label: t('Report'),
      });
    }

    if (canDeleteComment(comment)) {
      actions.push({
        danger: true,
        icon: <Delete2LineIcon height={16} width={16} />,
        key: 'delete',
        label: t('Delete'),
      });
    }

    return actions;
  };

  return (
    <Space orientation="horizontal" size="small">
      <Button
        icon={comment.bookmarked ? <BookmarkFillIcon /> : <BookmarkLineIcon />}
        loading={isBookmarking || isUnbookmarking}
        onClick={() => {
          handleBookmark(comment);
        }}
        type="link-secondary"
      />
      <Dropdown
        menu={{
          items: menu(),
          async onClick(key) {
            await onSelectKey(key);
          },
        }}
        trigger={['click']}
      >
        <Button icon={<More2LineIcon />} loading={isDeleting} type="link-secondary" />
      </Dropdown>
      {contextHolder}
    </Space>
  );
};

export default CommentActions;
